
import { useState } from 'react';
import LogoUploader from '../../../components/base/LogoUploader';

export default function TechnicalOptimization() {
  const [logo, setLogo] = useState('');
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogoChange = (logoUrl: string) => {
    setLogo(logoUrl);
    setShowLogoUploader(false);
    // Сохраняем логотип в localStorage для сохранения между сессиями
    if (logoUrl) {
      localStorage.setItem('siteLogo', logoUrl);
    } else {
      localStorage.removeItem('siteLogo');
    }
  };

  // Загружаем логотип при инициализации
  useState(() => {
    const savedLogo = localStorage.getItem('siteLogo');
    if (savedLogo) {
      setLogo(savedLogo);
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              {logo ? (
                <img 
                  src={logo} 
                  alt="Логотип" 
                  className="h-10 w-auto object-contain cursor-pointer"
                  onClick={() => setShowLogoUploader(true)}
                />
              ) : (
                <div 
                  onClick={() => setShowLogoUploader(true)}
                  className="text-2xl font-bold cursor-pointer hover:text-blue-600 transition-colors"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  logo
                </div>
              )}
              <button
                onClick={() => setShowLogoUploader(true)}
                className="text-sm text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
                title="Изменить логотип"
              >
                <i className="ri-edit-line"></i>
              </button>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Главная</a>
              <a href="/blog" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Блог</a>
              <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Услуги</a>
              <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Контакты</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Logo Uploader Modal */}
      {showLogoUploader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Загрузить логотип</h3>
              <button
                onClick={() => setShowLogoUploader(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <LogoUploader 
              onLogoChange={handleLogoChange}
              currentLogo={logo}
            />
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowLogoUploader(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Техническая оптимизация сайта
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Устраняем технические проблемы, которые мешают вашему сайту занимать высокие позиции в поисковых системах
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer">
                  Заказать аудит
                </button>
                <button className="border border-purple-600 text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors whitespace-nowrap cursor-pointer">
                  Посмотреть примеры
                </button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Technical%20SEO%20optimization%20dashboard%20showing%20website%20performance%20metrics%2C%20page%20speed%20analysis%2C%20crawl%20errors%2C%20and%20technical%20improvements%20with%20modern%20interface%20design%20and%20clean%20white%20background&width=600&height=400&seq=tech-seo-hero&orientation=landscape"
                alt="Техническая оптимизация"
                className="rounded-lg shadow-xl object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Технические проблемы, которые мы решаем
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Даже небольшие технические ошибки могут серьезно влиять на позиции сайта
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-red-50 border border-red-200 p-6 rounded-xl">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-speed-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Медленная загрузка</h3>
              <p className="text-gray-600 text-sm">
                Оптимизируем скорость загрузки страниц до рекомендуемых Google значений
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 p-6 rounded-xl">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-smartphone-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Проблемы с мобильной версией</h3>
              <p className="text-gray-600 text-sm">
                Исправляем ошибки адаптивности и улучшаем мобильное юзабилити
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bug-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Ошибки индексации</h3>
              <p className="text-gray-600 text-sm">
                Устраняем проблемы с robots.txt, sitemap.xml и другими файлами
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-link-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Битые ссылки</h3>
              <p className="text-gray-600 text-sm">
                Находим и исправляем все 404 ошибки и неработающие ссылки
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 p-6 rounded-xl">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Проблемы с HTTPS</h3>
              <p className="text-gray-600 text-sm">
                Настраиваем безопасное соединение и исправляем смешанный контент
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-6 rounded-xl">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-code-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Проблемы с разметкой</h3>
              <p className="text-gray-600 text-sm">
                Исправляем HTML-код и добавляем структурированные данные
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Как мы проводим техническую оптимизацию
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Системный подход к решению технических проблем
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Технический аудит</h3>
              <p className="text-gray-600">
                Полный анализ сайта с помощью профессиональных инструментов
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Приоритизация</h3>
              <p className="text-gray-600">
                Определяем критичные проблемы и составляем план работ
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Исправление</h3>
              <p className="text-gray-600">
                Устраняем найденные проблемы согласно лучшим практикам
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Контроль</h3>
              <p className="text-gray-600">
                Проверяем результаты и мониторим показатели сайта
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Инструменты, которые мы используем
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Профессиональные сервисы для глубокого анализа и оптимизации
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            <div className="text-center">
              <div className="bg-gray-50 p-6 rounded-xl mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Google%20PageSpeed%20Insights%20logo%20icon%20with%20clean%20white%20background%2C%20professional%20SEO%20tool%20branding&width=80&height=80&seq=pagespeed&orientation=squarish"
                  alt="PageSpeed Insights"
                  className="w-12 h-12 mx-auto object-top"
                />
              </div>
              <p className="text-sm font-medium text-gray-700">PageSpeed Insights</p>
            </div>

            <div className="text-center">
              <div className="bg-gray-50 p-6 rounded-xl mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Google%20Search%20Console%20logo%20icon%20with%20clean%20white%20background%2C%20professional%20SEO%20tool%20branding&width=80&height=80&seq=gsc&orientation=squarish"
                  alt="Search Console"
                  className="w-12 h-12 mx-auto object-top"
                />
              </div>
              <p className="text-sm font-medium text-gray-700">Search Console</p>
            </div>

            <div className="text-center">
              <div className="bg-gray-50 p-6 rounded-xl mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Screaming%20Frog%20SEO%20spider%20tool%20logo%20icon%20with%20clean%20white%20background%2C%20professional%20SEO%20software%20branding&width=80&height=80&seq=screamingfrog&orientation=squarish"
                  alt="Screaming Frog"
                  className="w-12 h-12 mx-auto object-top"
                />
              </div>
              <p className="text-sm font-medium text-gray-700">Screaming Frog</p>
            </div>

            <div className="text-center">
              <div className="bg-gray-50 p-6 rounded-xl mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=GTmetrix%20website%20performance%20testing%20tool%20logo%20icon%20with%20clean%20white%20background%2C%20professional%20SEO%20branding&width=80&height=80&seq=gtmetrix&orientation=squarish"
                  alt="GTmetrix"
                  className="w-12 h-12 mx-auto object-top"
                />
              </div>
              <p className="text-sm font-medium text-gray-700">GTmetrix</p>
            </div>

            <div className="text-center">
              <div className="bg-gray-50 p-6 rounded-xl mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Lighthouse%20Google%20tool%20logo%20icon%20with%20clean%20white%20background%2C%20professional%20web%20performance%20testing%20branding&width=80&height=80&seq=lighthouse&orientation=squarish"
                  alt="Lighthouse"
                  className="w-12 h-12 mx-auto object-top"
                />
              </div>
              <p className="text-sm font-medium text-gray-700">Lighthouse</p>
            </div>

            <div className="text-center">
              <div className="bg-gray-50 p-6 rounded-xl mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Ahrefs%20SEO%20tool%20logo%20icon%20with%20clean%20white%20background%2C%20professional%20SEO%20software%20branding&width=80&height=80&seq=ahrefs&orientation=squarish"
                  alt="Ahrefs"
                  className="w-12 h-12 mx-auto object-top"
                />
              </div>
              <p className="text-sm font-medium text-gray-700">Ahrefs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Стоимость технической оптимизации
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Фиксированная стоимость в зависимости от сложности проекта
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Базовая оптимизация</h3>
                <div className="text-4xl font-bold text-gray-900 mb-2">25 000₽</div>
                <p className="text-gray-600">единоразово</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Технический аудит</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Исправление критичных ошибок</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Оптимизация скорости</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Настройка robots.txt</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Создание sitemap.xml</span>
                </li>
              </ul>
              <button className="w-full bg-gray-100 text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer">
                Заказать
              </button>
            </div>

            <div className="bg-purple-600 text-white rounded-xl p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Рекомендуем
                </span>
              </div>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">Полная оптимизация</h3>
                <div className="text-4xl font-bold mb-2">45 000₽</div>
                <p className="text-purple-100">единоразово</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Все из базового пакета</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Структурированные данные</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Мобильная оптимизация</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Настройка HTTPS</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Исправление всех ошибок</span>
                </li>
              </ul>
              <button className="w-full bg-white text-purple-600 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                Заказать
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Премиум</h3>
                <div className="text-4xl font-bold text-gray-900 mb-2">75 000₽</div>
                <p className="text-gray-600">единоразово</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Все из полного пакета</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Индивидуальные доработки</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Консультации разработчика</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Приоритетная поддержка</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-3"></i>
                  <span className="text-gray-700">Гарантия 6 месяцев</span>
                </li>
              </ul>
              <button className="w-full bg-gray-100 text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer">
                Заказать
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Заказать техническую оптимизацию
            </h2>
            <p className="text-xl text-gray-600">
              Оставьте заявку и получите бесплатный экспресс-аудит вашего сайта
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Ваше имя *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Введите ваше имя"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                    Сайт *
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="https://yoursite.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Описание проблем (если знаете)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                  placeholder="Опишите известные проблемы с сайтом..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Максимум 500 символов</p>
              </div>

              <button
                type="submit"
                className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Заказать техническую оптимизацию
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              {logo ? (
                <img 
                  src={logo} 
                  alt="Логотип" 
                  className="h-12 w-auto object-contain mb-4"
                />
              ) : (
                <h3 className="text-xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                  logo
                </h3>
              )}
              <p className="text-gray-400">
                Профессиональное SEO продвижение сайтов с гарантией результата
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Услуги</h4>
              <ul className="space-y-2">
                <li><a href="/services/seo-audit" className="text-gray-400 hover:text-white transition-colors cursor-pointer">SEO Аудит</a></li>
                <li><a href="/services/technical-optimization" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Техническая оптимизация</a></li>
                <li><a href="/services/content-marketing" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Контент-маркетинг</a></li>
                <li><a href="/services/linkbuilding" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Линкбилдинг</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Компания</h4>
              <ul className="space-y-2">
                <li><a href="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Блог</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">О нас</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Кейсы</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Контакты</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Контакты</h4>
              <ul className="space-y-2 text-gray-400">
                <li>+7 (950) 360-35-20</li>
                <li>info@seo-agency.ru</li>
                <li>Москва, ул. Примерная, 123</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 SEO Agency. Все права защищены. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
